// Tour pelo app: destaca cada parte das telas principais com uma explicação curta.
// Só navega entre telas (não abre cobrança nem mexe em dados). Aparece uma vez para a dona; depois, em Mais.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { can, useMe } from '../lib/data.ts';
import { first } from '../lib/format.ts';
import { Icon } from '../ui/Icon.tsx';
import { useNav, type View } from './nav.tsx';

interface Step { ch: string; view: View; sel: string; t: string; p: string; perm?: string }
const STEPS: Step[] = [
  { ch: 'Hoje', view: 'hoje', sel: '.today-head', t: 'Seu dia num olhar', p: 'A Hoje mostra quem vem, quem está na cadeira e os horários livres, em ordem.' },
  { ch: 'Hoje', view: 'hoje', sel: '.river', t: 'A agenda do dia', p: 'Toque num cartão para ver tudo sobre o horário: chegou, cobrar, remarcar ou cancelar. Em "Livre", toque em Encaixar.' },
  { ch: 'Hoje', view: 'hoje', sel: '.hd-actions .bell', t: 'Avisos', p: 'Agendou pelo link, disse que pagou o sinal, confirmou, cancelou, avaliou. Toque para ir direto ao que aconteceu.' },
  { ch: 'Hoje', view: 'hoje', sel: '.hd-actions .brand', t: 'Agendar em segundos', p: 'O + abre o novo agendamento de qualquer tela: cliente, serviço, horário e pronto.' },
  { ch: 'Hoje', view: 'hoje', sel: '.ret-card', t: 'Para chamar de volta', p: 'Quem está com o retorno do serviço vencendo e ainda não marcou. Um convite pelo WhatsApp e o link.' },
  { ch: 'Agenda', view: 'agenda', sel: '.seg', t: 'Dia, semana e mês', p: 'No dia, uma coluna por parceira; toque num espaço vazio para agendar ali. Na semana e no mês, a ocupação de cada dia.' },
  { ch: 'Clientes', view: 'clientes', sel: '.search-in', t: 'Ache qualquer cliente', p: 'Pelo nome ou pelo telefone, com ou sem acento. A ficha tem histórico, anamnese, pacotes, selos e fotos.' },
  { ch: 'Caixa', view: 'caixa', sel: '.hoje-enter .heading, .heading', t: 'O caixa do dia', p: 'Tudo o que entrou, por forma de pagamento, com as taxas. No fim do dia, conte a gaveta e feche.', perm: 'valoresSalao' },
  { ch: 'Mais', view: 'mais', sel: '.mais-view .gl', t: 'O resto do salão', p: 'Pagar depois, vendas no balcão, pacotes, vale-presente, fidelidade, relatórios, despesas e as configurações.' },
];
const SEEN = 'sublime-tour-visto';
export const openTour = () => window.dispatchEvent(new Event('sublime-tour'));

// fica dentro da navegação: abre sozinho na primeira vez da dona, ou pelo Mais
export function TourHost() {
  const [tour, setTour] = useState<null | { again: boolean }>(null);
  const open = useCallback(() => setTour({ again: false }), []);
  useTourAuto(open);
  useEffect(() => { const f = () => setTour({ again: true }); window.addEventListener('sublime-tour', f); return () => window.removeEventListener('sublime-tour', f); }, []);
  return tour ? <Tour again={tour.again} onEnd={() => setTour(null)} /> : null;
}

export function useTourAuto(open: () => void) {
  const owner = can(useMe().data, 'config');
  useEffect(() => {
    if (!owner) return;
    try { if (localStorage.getItem(SEEN)) return; } catch { return; }
    // marca como visto só quando abre de verdade (se a tela mudar antes, tenta de novo)
    const t = window.setTimeout(() => { try { localStorage.setItem(SEEN, '1'); } catch { /* sem armazenamento */ } open(); }, 900);
    return () => window.clearTimeout(t);
  }, [owner, open]);
}

export function Tour({ onEnd, again }: { onEnd: () => void; again?: boolean }) {
  const nav = useNav();
  const me = useMe().data!;
  const steps = STEPS.filter((s) => !s.perm || can(me, s.perm));
  const chapters = [...new Set(steps.map((s) => s.ch))];
  const [i, setI] = useState(-1);
  const [box, setBox] = useState<{ x: number; y: number; w: number; h: number; tip: number } | null>(null);
  const lay = useRef<HTMLDivElement>(null);
  const tip = useRef<HTMLDivElement>(null);
  const st = i >= 0 ? steps[i] : null;

  useEffect(() => { if (st && nav.view !== st.view) nav.go(st.view); }, [st, nav]);
  // destaca o elemento e põe o balão perto, sem sair da tela; o que não existe nesta tela é pulado
  useLayoutEffect(() => {
    if (!st) { setBox(null); return; }
    let tries = 0, timer = 0;
    const place = () => {
      const el = document.querySelector(st.sel);
      const L = lay.current?.getBoundingClientRect();
      if (!L) return;
      if (!el || !el.getBoundingClientRect().height) {
        if (tries++ < 8) { timer = window.setTimeout(place, 120); return; }
        setI((n) => (n + 1 < steps.length ? n + 1 : n)); return;
      }
      // elemento maior que meia tela (a agenda do dia): mostra o começo dele e o balão logo abaixo
      const tall = el.getBoundingClientRect().height > L.height * 0.45;
      el.scrollIntoView({ block: tall ? 'start' : 'center' });
      const r = el.getBoundingClientRect(), pad = 6;
      let y = r.top - L.top - pad, h = r.height + pad * 2;
      if (y < 8) { h -= 8 - y; y = 8; }
      if (y + h > L.height - 8) h = L.height - 8 - y;
      if (tall) h = Math.min(h, L.height * 0.38);
      const th = tip.current?.offsetHeight ?? 180, below = y + h + 12, above = y - 12 - th;
      setBox({ x: r.left - L.left - pad, y, w: r.width + pad * 2, h: Math.max(h, 24), tip: below + th < L.height - 12 ? below : above >= 12 ? above : L.height - th - 16 });
    };
    timer = window.setTimeout(place, 160);
    return () => window.clearTimeout(timer);
  }, [st, steps.length]);

  const end = () => { onEnd(); };
  if (!st) {
    return (
      <div className="tour center" role="dialog" aria-modal="true" aria-labelledby="tourT" ref={lay}><div className="tour-hole" aria-hidden="true" />
        <div className="tour-tip tour-menu" ref={tip}><span className="tour-eyebrow">{again ? 'Tour pelo app' : `Boas-vindas, ${first(me.user.name)}`}</span>
          <h3 id="tourT">{again ? 'O que você quer rever?' : 'Vamos conhecer o app?'}</h3>
          <p>{chapters.length} partes, uns 2 minutos. Comece do início ou escolha uma parte.</p>
          <div className="tour-chs">{chapters.map((c) => { const n = steps.filter((s) => s.ch === c).length; return <button key={c} onClick={() => setI(steps.findIndex((s) => s.ch === c))}><b>{c}</b><small>{n} {n > 1 ? 'passos' : 'passo'}</small></button>; })}</div>
          <div className="tour-foot"><button className="tour-skip" onClick={end}>{again ? 'Fechar' : 'Agora não'}</button><button className="cta sm" autoFocus onClick={() => setI(0)}>Começar do início</button></div></div></div>
    );
  }
  const inCh = steps.filter((s) => s.ch === st.ch), pos = inCh.indexOf(st) + 1, last = i === steps.length - 1;
  const next = !last ? steps[i + 1]! : null;
  return (
    <div className="tour" role="dialog" aria-modal="true" aria-labelledby="tourT" aria-describedby="tourP" ref={lay}>
      <div className="tour-hole" aria-hidden="true" style={box ? { left: box.x, top: box.y, width: box.w, height: box.h, borderRadius: Math.min(20, box.h / 2) } : { opacity: 0 }} />
      <div className="tour-tip" ref={tip} style={box ? { top: box.tip } : undefined}>
        <i className="tour-bar" aria-hidden="true"><i style={{ width: `${Math.round(((i + 1) / steps.length) * 100)}%` }} /></i>
        <span className="tour-eyebrow"><button className="tour-chlink" onClick={() => setI(-1)} aria-label="Ver as partes do tour">{st.ch}</button> · {pos} de {inCh.length}</span>
        <h3 id="tourT">{st.t}</h3><p id="tourP">{st.p}</p>
        <div className="tour-foot"><button className="tour-skip" onClick={end}>Sair</button>
          {i > 0 && <button className="tour-back" onClick={() => setI(i - 1)} aria-label="Passo anterior"><Icon name="back" /></button>}
          <button className="cta sm" autoFocus onClick={() => (last ? end() : setI(i + 1))}>{last ? 'Terminar' : next!.ch !== st.ch ? `Ir para ${next!.ch}` : 'Próximo'}</button></div>
      </div>
    </div>
  );
}
